import { type FC } from 'react'

import classNames from 'classnames'
import { usePathname, useRouter } from 'next/navigation'

import { CollapsibleGroup } from '../CollapsibleGroup/CollapsibleGroup'
import { NavItem } from '../NavItem'
import { getNavigationItemActive } from '../utils'

import type { NavigationItem } from '../types'

export interface NavGroupProps {
  item: NavigationItem
  index: number
}

export const NavGroup: FC<NavGroupProps> = ({ item, index }) => {
  const pathname = usePathname()
  const router = useRouter()

  if (item.isHidden) return null

  const { parentActive, parentHasActiveChild } = getNavigationItemActive(
    item,
    pathname,
  )

  // Use the isCollapsible field to determine component type
  if (!item.isCollapsible) {
    return (
      <div className={classNames({ 'mt-4': index === 0 })}>
        <NavItem
          active={parentActive}
          className={classNames(
            'mb-1 p-0.5 [&_.inner]:rounded-sm [&_.inner]:px-2',
          )}
          href={item.fallbackUrl ?? item.url}
          icon={item.icon}
          label={item.label}
        />
      </div>
    )
  }

  const handleNavigate = (href: string) => {
    // Only navigate if we're not already on this page
    if (!parentActive) {
      router.push(href)
    }
    // If parentActive is true, do nothing to allow toggling
  }

  return (
    <div
      className={classNames('mb-1 p-0.5', {
        'mt-4': index === 0,
      })}
    >
      <CollapsibleGroup
        active={parentActive}
        defaultCollapsedState={!(parentHasActiveChild || parentActive)}
        groupName={item.label}
        href={!parentActive ? (item.fallbackUrl ?? item.url) : undefined}
        onNavigate={!parentActive ? handleNavigate : undefined}
      >
        {item.children?.map((groupItem, subIndex) => {
          if ('url' in groupItem) {
            if (groupItem.isHidden) return null

            return (
              <CollapsibleGroup.Item
                key={`${item.label}-${index}-${subIndex}`}
                active={
                  getNavigationItemActive(groupItem, pathname).parentActive
                }
                className="mb-0 [&_.inner]:p-2 [&_.inner]:pl-6"
                href={groupItem.url}
              >
                {groupItem.label}
              </CollapsibleGroup.Item>
            )
          }
        })}
      </CollapsibleGroup>
    </div>
  )
}
